import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useCallback, useState } from "react";

import { Dialog } from "~ui/atoms/Dialog/Dialog";
import {
  Form,
  formatSelectValue,
  type FormProps,
  getParsedSelected,
  SubmitButton,
  type SwitchValue,
  type TextFieldValue,
} from "~ui/atoms/Form";
import {
  BubbleSelect,
  Description,
  FIELD_NAME_BUBBLE_SELECT,
  FIELD_NAME_DESCRIPTION,
  FIELD_NAME_IS_AUTOJOIN,
  FIELD_NAME_IS_SEARCHABLE,
  FIELD_NAME_ROOM_OR_EVENT_NAME,
  IsAutojoin,
  IsSearchable,
  RoomOrEventName,
} from "~ui/molecules/Form";

interface Event {
  bubbleId: string;
  description: string;
  isAutoJoin: boolean;
  isSearchable: boolean;
  name: string;
}

interface EventFormModalProps {
  bubbleSelectItems?: { id: string; value: string }[];
  /** When false, hides the create-event entry point (bubble permissions). */
  createEventsAllowed?: boolean;
  defaultBubbleId?: string;
  initialValues?: Event;
  mode: "create" | "edit";
  onSubmit: (event: Event) => void;
}

interface FormValues {
  [FIELD_NAME_BUBBLE_SELECT]: string;
  [FIELD_NAME_DESCRIPTION]: TextFieldValue;
  [FIELD_NAME_IS_AUTOJOIN]: SwitchValue;
  [FIELD_NAME_IS_SEARCHABLE]: SwitchValue;
  [FIELD_NAME_ROOM_OR_EVENT_NAME]: TextFieldValue;
}

const tCancel = "Cancel";
const tCreate = "Create Event";
const tUpdate = "Update Event";
const tAddNewEvent = "Create";
const tEditEvent = "Edit Event";
const tAutojoinTooltip = "Toggle ON to allow people to join your event without needing their request approved.";
const tSearchableTooltip = "Toggle ON to make your event searchable.";

const requiredFields: (keyof FormValues)[] = ["roomOrEventName", "description"];

export const EventFormModal = ({
  mode,
  onSubmit,
  bubbleSelectItems = [],
  createEventsAllowed = true,
  defaultBubbleId,
  initialValues,
}: EventFormModalProps) => {
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback(() => setOpen(false), []);
  const openDialog = useCallback(() => setOpen(true), []);

  // Prepare initial bubble value for form
  const initialBubbleSelectValue = defaultBubbleId
    ? bubbleSelectItems.find((x) => x.id === defaultBubbleId)
    : initialValues
      ? bubbleSelectItems.find((x) => x.id === initialValues.bubbleId)
      : undefined;

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues, form) => {
      const name = formValues[FIELD_NAME_ROOM_OR_EVENT_NAME];
      const description = formValues[FIELD_NAME_DESCRIPTION];

      if (name && description) {
        const bubbleId = formValues[FIELD_NAME_BUBBLE_SELECT]
          ? String(getParsedSelected(formValues[FIELD_NAME_BUBBLE_SELECT]).id)
          : "";

        if (!bubbleId) {
          console.error("Cannot create/update event without bubbleId");
          return;
        }

        const event: Event = {
          bubbleId,
          description,
          isAutoJoin:
            Array.isArray(formValues[FIELD_NAME_IS_AUTOJOIN]) && formValues[FIELD_NAME_IS_AUTOJOIN].length > 0,
          isSearchable:
            Array.isArray(formValues[FIELD_NAME_IS_SEARCHABLE]) && formValues[FIELD_NAME_IS_SEARCHABLE].length > 0,
          name,
        };

        onSubmit(event);
        // Close before reset: form.reset() triggers a re-render that can interfere with the
        // Dialog's close transition and cause the container to persist in the DOM briefly
        closeDialog();
        form?.reset();
      }
    },
    [onSubmit, closeDialog],
  );

  const isCreate = mode === "create";
  const title = isCreate ? tAddNewEvent : tEditEvent;
  const submitText = isCreate ? tCreate : tUpdate;

  if (isCreate && !createEventsAllowed) {
    return null;
  }

  return (
    <>
      {isCreate ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openDialog}
          fullWidth
          data-testid="create-event-button"
          sx={{
            "&:hover": {
              background: "linear-gradient(135deg, #43A047 0%, #1E88E5 100%)",
            },
            background: "linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)",
            borderRadius: "12px",
            color: "white",
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          {tAddNewEvent}
        </Button>
      ) : (
        <IconButton
          onClick={openDialog}
          size="small"
          data-testid="edit-event-button"
          sx={{
            "&:hover": {
              backgroundColor: "action.selected",
            },
            backgroundColor: "action.hover",
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}

      <Form<FormValues>
        onSubmit={handleSubmit}
        initialValues={
          initialValues
            ? {
                [FIELD_NAME_BUBBLE_SELECT]: initialBubbleSelectValue
                  ? formatSelectValue(initialBubbleSelectValue)
                  : undefined,
                [FIELD_NAME_DESCRIPTION]: initialValues.description,
                [FIELD_NAME_IS_AUTOJOIN]: initialValues.isAutoJoin ? [FIELD_NAME_IS_AUTOJOIN] : undefined,
                [FIELD_NAME_IS_SEARCHABLE]: initialValues.isSearchable ? [FIELD_NAME_IS_SEARCHABLE] : undefined,
                [FIELD_NAME_ROOM_OR_EVENT_NAME]: initialValues.name,
              }
            : undefined
        }
      >
        <Dialog
          actions={
            <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, auto)" gridAutoFlow="row" padding="1rem">
              <Box display="grid" gap="3rem" gridAutoColumns="1fr" gridAutoFlow="column">
                <Tooltip placement="top" title={tAutojoinTooltip}>
                  <span>
                    <IsAutojoin
                      useDefault={false}
                      initialValue={initialValues?.isAutoJoin ? [FIELD_NAME_IS_AUTOJOIN] : undefined}
                    />
                  </span>
                </Tooltip>
                <Tooltip placement="top" title={tSearchableTooltip}>
                  <span>
                    <IsSearchable initialValue={initialValues?.isSearchable ? [FIELD_NAME_IS_SEARCHABLE] : undefined} />
                  </span>
                </Tooltip>
              </Box>

              <BubbleSelect
                initialValue={initialBubbleSelectValue ? formatSelectValue(initialBubbleSelectValue) : undefined}
                label="Host Organization"
                required
                selectItems={bubbleSelectItems}
              />

              <RoomOrEventName validationOptions={{ maxLength: 100 }} />

              <Description />

              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button onClick={closeDialog} variant="outlined">
                  {tCancel}
                </Button>
                <SubmitButton
                  requiredFields={[...requiredFields, "bubbleSelect"]}
                  variant="contained"
                  data-testid="submit-event-button"
                >
                  {submitText}
                </SubmitButton>
              </Box>
            </Box>
          }
          dialogType="custom"
          maxWidth="sm"
          onDialogClose={closeDialog}
          open={open}
          title={title}
        />
      </Form>
    </>
  );
};
